import { AfterViewInit, OnDestroy, ViewChild } from "@angular/core";
import { Component } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute } from "@angular/router";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { LocalizationTranslationCreateModalComponent } from "src/app/components/admin/localization/localization-translation-create-modal/localization-translation-create-modal.component";
import LocalizationConstants from "src/app/constants/localization-constants";
import RouteNameConstants from "src/app/constants/route-name-constants";
import { Translation } from "src/app/models/Translation";
import { GlobalOverlayService } from "src/app/services/global-overlay.service";
import { LocalizationService } from "src/app/services/localization.service";
import { ModalService } from "src/app/services/modal.service";
import { StorageService } from "src/app/services/storage.service";

import { AuthService } from "../../../services/auth.service";
import { ConfirmationDialogData } from "../../common/confirmation-dialog/confirmation-dialog-modal.component";

@Component({
  selector: "app-localization-edit",
  templateUrl: "./localization-edit.component.html",
  styleUrls: ["./localization-edit.component.scss"],
  standalone: false,
})
export class LocalizationEditComponent implements OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  readonly routeNameConstants = RouteNameConstants;
  readonly localizationConstants = LocalizationConstants;

  private subscriptions: Subscription[] = [];
  faSpinner = faSpinner;
  language: string;
  dataSource = new MatTableDataSource<Translation>([]);
  isDefaultLanguage: boolean;

  pageSize: number = 20;
  page: number = 1;

  quickSearch: string = "";

  columnsToDisplayAll = ["key", "englishValue", "translatedValue", "action1", "action2"];
  columnsToDisplayDefault = this.columnsToDisplayAll.filter((a) => a !== "englishValue");

  constructor(
    private route: ActivatedRoute,
    private localizationService: LocalizationService,
    public modalService: ModalService,
    public authService: AuthService,
    private globalOverlayService: GlobalOverlayService,
    private storageService: StorageService,
  ) {
    this.language = this.route.snapshot.paramMap.get("id");
    if (!this.language) return;

    this.isDefaultLanguage = localizationService.isDefaultLanguage(this.language);
    this.getLocalizationList(this.language);
  }

  get columnsToDisplay() {
    return this.isDefaultLanguage ? this.columnsToDisplayDefault : this.columnsToDisplayAll;
  }

  ngAfterViewInit() {
    this.dataSource.filterPredicate = this.quickSearchPredicate;
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  getLocalizationList(language: string) {
    this.globalOverlayService.show();
    this.localizationService
      .getLocalizationListForLanguage(language)
      .pipe(finalize(() => this.globalOverlayService.close()))
      .subscribe((response) => {
        this.dataSource.data = response;
      });
  }

  addLocalization(item: Translation) {
    item.translatedValue = item.newValue;
    if (!item.translatedValue) return;

    item.isItemLoading = true;
    this.localizationService
      .addLocalization(this.language, { key: item.key, value: item.newValue })
      .subscribe({
        next: () => {
          item.isItemLoading = false;
          item.newValue = null;
        },
        error: () => this.revertItemState(item, null),
      });
  }

  bulkAddLocalization() {
    const list = this.dataSource.data
      .filter((x) => x.newValue && !x.translatedValue)
      .map((x) => {
        return { key: x.key, value: x.newValue };
      });
    this.globalOverlayService.show();
    this.dataSource.data
      .filter((x) => x.newValue && !x.translatedValue)
      .forEach((x) => (x.translatedValue = x.newValue));

    this.localizationService
      .bulkAddLocalization(this.language, list)
      .pipe(finalize(() => this.globalOverlayService.close()))
      .subscribe({
        next: () =>
          this.dataSource.data.filter((x) => x.newValue).forEach((x) => (x.newValue = null)),
        error: () => this.getLocalizationList(this.language),
      });
  }

  updateLocalization(item: Translation) {
    const oldValue = item.translatedValue;
    item.translatedValue = item.newValue;

    item.isItemLoading = true;

    this.localizationService
      .updateLocalization(this.language, { key: item.key, value: item.newValue })
      .subscribe({
        next: async () => await this.resetItemState(item),
        error: () => this.revertItemState(item, oldValue),
      });
  }

  private deleteLocalization(item: Translation) {
    const oldValue = item.translatedValue;

    //* If language is EN or English phrase is empty need to delete record from the list
    //* Otherwise clear translated phrase only

    const deleteRecord = this.isDefaultLanguage || !item.englishValue;
    if (deleteRecord) this.dataSource.data = this.dataSource.data.filter((x) => x.key !== item.key);
    else item.translatedValue = null;
    item.newValue = null;
    this.localizationService.deleteLocalization(this.language, item.key).subscribe({
      next: async () => await this.refreshLocalizationAsync(this.language),
      error: () => {
        if (deleteRecord) {
          const index = this.dataSource.data.indexOf(item);
          this.dataSource.data.splice(index, 0, item);
        } else this.revertItemState(item, oldValue);
      },
    });
  }

  onDeleteLocalization(item: Translation) {
    const resultObservable = this.modalService.openConfirmationModal(
      new ConfirmationDialogData(LocalizationConstants.LOCALIZATION_REMOVE_DIALOG_TITLE, [
        LocalizationConstants.LOCALIZATION_REMOVE_DIALOG_CONTENT,
      ]),
    );

    const subscription = resultObservable.subscribe((result) => {
      if (result) {
        this.deleteLocalization(item);
      }
    });

    this.subscriptions.push(subscription);
  }

  addNewKeyTranslation(code, value) {
    this.modalService.close();
    this.dataSource.data.splice(0, 0, new Translation(code, value, value));

    this.localizationService.addLocalization(this.language, { key: code, value: value }).subscribe({
      next: async () => await this.refreshLocalizationAsync(this.language),
      error: () => this.dataSource.data.splice(0, 1),
    });
  }

  private async refreshLocalizationAsync(languageCode: string): Promise<void> {
    this.storageService.clearLocalization(languageCode);
    this.storageService.clearLanguageList();
    await this.localizationService.setLocalization(languageCode);
  }

  trackByFn(index, item) {
    return item.name;
  }

  private revertItemState(item, oldValue) {
    item.translatedValue = oldValue;
    item.newValue = null;
    item.isItemLoading = false;
  }

  private async resetItemState(item: Translation): Promise<void> {
    item.isItemLoading = false;
    item.isEditing = false;
    item.newValue = null;

    await this.refreshLocalizationAsync(this.language);
  }

  get bulkLength() {
    return this.dataSource.data.filter((x) => x.newValue && !x.translatedValue)?.length;
  }

  get showBulkAdd() {
    return this.bulkLength >= 3;
  }

  private quickSearchPredicate(translation: Translation, searchValue: string): boolean {
    if (!searchValue) return true;
    searchValue = searchValue.toLowerCase();
    return (
      translation.key?.toLowerCase().includes(searchValue) ||
      translation.englishValue?.toLowerCase().includes(searchValue) ||
      translation.translatedValue?.toLowerCase().includes(searchValue)
    );
  }

  openAddTranslationModal() {
    this.modalService
      .openDefaultModal(LocalizationTranslationCreateModalComponent)
      .subscribe((result) => {
        if (!result) return;
        this.addNewKeyTranslation(result.code, result.translation);
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }
}
